import { createFileRoute } from "@tanstack/react-router";
import { ResearchBlock } from "@/components/site/ResearchBlock";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  { q: "När behövs 3D-animation?", a: "När produkten är fysisk men inte tillgänglig (prototyp, för stor, för dyr att frakta), när ni vill visa insidan av något, eller när scenarios är farliga/omöjliga att filma. Industri, medtech och B2B-produkt är typiska fall." },
  { q: "Vad kostar 3D-animation?", a: "Enkel produktvisualisering på vit bakgrund (15–30 sek): 60–150 kkr. Komplex 3D med scenario och miljö: 200–600 kkr. Photoreal med ljus-simulering och full CGI-miljö: från 500 kkr." },
  { q: "Hur lång produktionstid?", a: "8–14 veckor för normal 3D-produktion. Modellering → texturering → rigging → animation → lighting → rendering → compositing. Inga genvägar — varje steg bygger på det förra." },
  { q: "Får vi CAD-filer eller måste ni bygga om?", a: "Vi tar gärna era CAD-filer som start (STEP, IGES, OBJ). Det halverar ofta modelleringstiden. Vid mer artistic render bygger vi ändå om — CAD är inte alltid optimerat för render." },
];

export const Route = createFileRoute("/3d-animation")({
  head: () => ({
    meta: [
      { title: "3D-produktvisualisering — se produkten innan den är byggd" },
      { name: "description", content: "3D-produktvisualisering från er CAD (STEP/IGES/OBJ): produktfilm, teknisk animation och still-renders. Se exempel från industri och medtech — klart på 3–8 veckor." },
      { property: "og:title", content: "3D-produktvisualisering — se produkten innan den är byggd" },
      { property: "og:description", content: "3D-animation, produktrendering och teknisk visualisering från er CAD. Pris och tidplan innan start." },

      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/3d-animation" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/3d-animation" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/3d-animation", name: "Produktvisualisering & 3D-animation från CAD | Fenny", description: "Produktvisualisering, produktrendering och 3D-animation för industri, medtech och B2B. Skicka CAD (STEP/IGES/OBJ) — ni får film och still-renders tillbaka."})) },
      { type: "application/ld+json", children: JSON.stringify(serviceSchema({ name: "3D-animation", path: "/3d-animation", serviceType: "3D-animation", description: "Produktvisualisering, industri-render och 3D-animation." })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "3D-animation", path: "/3d-animation" }])) },
    ],
  }),
  component: () => (
    <>
      <LandingPage
      eyebrow="3D-animation"
      title={<>3D-animation när verkligheten inte räcker som verktyg.</>}
      lede="Vi producerar 3D-animation för produkter, industri-utrustning och medtech. Photoreal när det krävs, stiliserat när det berättar bättre. CAD-filer in, mastrad film ut."
      tldr={<p>3D-animation används för produktvisualisering, arkitektur, teknik och CGI-reklam. Pris: <strong>40 000–500 000 kr</strong> beroende på komplexitet, render-tid och längd. Fenny producerar 3D-animation som Prime-projekt med modellering, texturering, animation och rendering inhouse/via partner. Söker ni still-renders och 360-vyer av en produkt? Se <a href="/3d-produktvisualisering" className="underline underline-offset-4">3D-produktvisualisering</a>.</p>}
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Motion graphics", to: "/motion-graphics" }}
      leadMagnet={{
        slug: "brief-mall",
        title: "Ladda ner: brief-mallen som inte slösar tid",
        description: "1 sida, 7 fält som styr utfallet, plus ett ifyllt exempel. Använd den innan ni tar in offerter.",
      }}
      trackingId="3d-animation"
      relatedLinksFor="/3d-animation"
      trust={[
        { value: "40–500 kkr", label: "typiskt prisspann" },
        { value: "4–10 v", label: "brief till leverans" },
        { value: "STEP/IGES/OBJ", label: "format vi tar emot" },
      ]}
      pricing={{
        heading: "Vad 3D-animation kostar",
        intro: "Riktpriser 2026. Det som styr priset är modellens komplexitet och hur mycket som måste byggas från grunden när CAD saknas.",
        rows: [
          { format: "Produktloop", scope: "Befintlig CAD, 15–30 sek rotation och närbilder, en miljö", price: "40 000–90 000 kr", lead: "3–4 veckor" },
          { format: "Teknisk förklaringsfilm", scope: "60–90 sek, sprängskiss, genomskärning, voice-over och textning", price: "120 000–280 000 kr", lead: "6–8 veckor" },
          { format: "CGI-reklamfilm", scope: "Photoreal, egen ljussättning, originalmusik, flera kanalversioner", price: "300 000–600 000 kr", lead: "8–12 veckor" },
        ],
        footnote: "Exkl. moms. Saknas CAD tillkommer modellering från ritning eller foto, typiskt 20 000–80 000 kr.",
      }}
      bullets={[
        { title: "CAD-vänligt", body: "STEP/IGES/OBJ in — vi optimerar för render utan att tappa precision." },
        { title: "Photoreal eller stiliserat", body: "Beslut drivs av målgrupp och budskap — inte 'vad som ser coolt ut'." },
        { title: "Modulär leverans", body: "Master + sprängskisser + loopar för mässa + still-renders till sajt." },
      ]}
      sections={[
        {
          id: "jamforelse",
          heading: "Innan ni väljer",
          body: (
            <ResearchBlock
              intro={"3D är det format där offerter skiljer sig mest. Innan ni jämför pris behöver ni veta vad ni faktiskt beställer."}
              steps={[
                { q: "Finns CAD-underlag?", a: "Med CAD-filer går modelleringen snabbt. Utan dem byggs modellen från foto och ritning — det är den största kostnadsposten." },
                { q: "Bestäm längd och detaljnivå", a: "30 sekunder produktvisualisering och två minuters teknisk förklaring är olika projekt, även om båda kallas 3D." },
                { q: "Fråga om ändringar", a: "Ändringar efter render kostar mest. Kolla hur många rundor som ingår före och efter slutrender." },
              ]}
              links={[
                { label: "3D-produktvisualisering", href: "/3d-produktvisualisering" },
                { label: "Animerad förklaringsfilm", href: "/animerad-forklaringsfilm" },
                { label: "Prisspann och exempel", href: "/priser" },
              ]}
            />
          ),
        },
        { id: "nar", heading: "När 3D slår live action", body: <p>När produkten inte finns ännu (prototyp). När den är för stor eller dyr att flytta. När ni vill visa insidan utan att klyva något. När scenariot är farligt eller omöjligt fysiskt. När fler produktvariant ska visas utan att fota varje för sig.</p> },
        { id: "stil", heading: "Stilval styrs av målgrupp", body: <p>Ingenjör vill se exakta materiel och toleranser — photoreal. Marknadschef vill ha berättelsekraft — stiliserat fungerar ofta bättre. Vi rekommenderar utifrån vem som ska titta, inte vad som vinner pris.</p> },
        { id: "leverans", heading: "Vad ni får ut", body: <p>Master-film, individuella komponenter (loopar för mässmonter), still-renders för sajt och säljmaterial, samt källfilen för framtida revisioner. En produktion, många användningsområden.</p> },
      ]}
      faq={FAQ}
      caseLimit={4}
      closing={{ title: "Har ni en produkt att visualisera?", body: "30 minuter där vi går igenom CAD-status, omfång och budget.", primary: { label: "Boka möte", to: "/kontakt" } }}
    />
      <RelatedKnowledgeAuto topics={["3d-animation", "motion-graphics", "animation", "pris", "explainer"]} />
    </>
  ),
});
